import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildWorkflowSearch } from "../workflows";
import { useNextWorkItem } from "../next";

/**
 * "Next" dispenser.
 *
 * A press locks and hands back the single most important due item across
 * the caller's queues. A fresh serve navigates to the item; that navigation
 * is the feedback. A re-serve, an empty outcome, and any transport error
 * surface as a transient notice the caller renders in a `Toast`.
 */

export type NextNoticeTone = "info" | "danger";

export interface NextNotice {
  readonly tone: NextNoticeTone;
  readonly text: string;
}

export interface NextDispenser {
  readonly handleNext: () => void;
  readonly isPending: boolean;
  readonly notice: NextNotice | null;
  readonly dismissNotice: () => void;
}

export function useNextDispenser(selectedQueueId: string | null): NextDispenser {
  const navigate = useNavigate();
  const nextMutation = useNextWorkItem();
  const [notice, setNotice] = useState<NextNotice | null>(null);

  const dismissNotice = useCallback(() => setNotice(null), []);

  const handleNext = useCallback(() => {
    if (nextMutation.isPending) return;
    nextMutation.mutate(undefined, {
      onSuccess: (result) => {
        const item = result.item;
        if (item === null) {
          setNotice({
            tone: "info",
            text: "You’re all caught up — nothing due right now.",
          });
          return;
        }
        if (item.entityType.length > 0 && item.entityId.length > 0) {
          const search = buildWorkflowSearch(item.entityType, item.entityId, selectedQueueId, {
            itemId: item.id,
          });
          void navigate(`/workflows/${item.workflowId}?${search}`);
        }
        if (result.reServed) {
          setNotice({
            tone: "info",
            text: "Back to the item you’re already working on.",
          });
        }
      },
      onError: (error: unknown) => {
        setNotice({
          tone: "danger",
          text: error instanceof Error ? error.message : "Couldn’t fetch the next item.",
        });
      },
    });
  }, [nextMutation, navigate, selectedQueueId]);

  return { handleNext, isPending: nextMutation.isPending, notice, dismissNotice };
}
